import { Link } from "react-router-dom";
import {
  Facebook, Instagram, Youtube, Twitter, Linkedin, Music2, MessageCircle,
  Link as LinkIcon, Eye, Heart,
} from "lucide-react";
import { toast } from "sonner";
import { Monogram } from "@/components/Monogram";
import { useSettings } from "@/context/SettingsContext";

const SOCIAL_META = [
  ["facebook", Facebook], ["instagram", Instagram], ["youtube", Youtube],
  ["x", Twitter], ["tiktok", Music2], ["linkedin", Linkedin], ["whatsapp", MessageCircle],
];

const MARQUEE = ["Faith", "Service", "Legacy", "Community", "Revival", "Mentorship", "Hope"];

export const Footer = ({ scrollToId }) => {
  const { social, stats } = useSettings();
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = "Charles McKuhn — Apostle, Prophetic Intercessor & Author";
  const activeSocials = SOCIAL_META.filter(([k]) => social && social[k]);
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Link copied");
  };
  return (
  <footer data-testid="site-footer" className="bg-ink text-parchment">
    <div className="overflow-hidden border-y border-white/10 py-6">
      <div className="flex w-max animate-marquee">
        {[...MARQUEE, ...MARQUEE].map((w, i) => (
          <span
            key={i}
            className="font-serif text-3xl sm:text-4xl italic text-parchment/30 px-8 flex items-center gap-8"
          >
            {w}
            <span className="text-gold-light not-italic">◆</span>
          </span>
        ))}
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <div className="flex items-center gap-3">
          <Monogram size={44} />
          <div>
            <div className="font-serif text-xl font-semibold">Charles McKuhn</div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-parchment/50">
              Author · Speaker · Community Leader
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-6 text-sm">
          {[
            ["about", "About"],
            ["beliefs", "Beliefs"],
            ["appointment", "Prophetic Prayer"],
            ["gallery", "Media"],
            ["books", "Books"],
            ["community", "Community"],
            ["events", "Events"],
            ["blog", "Journal"],
            ["bible", "Scripture"],
            ["give", "Donate"],
            ["faq", "FAQ"],
            ["contact", "Contact"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`footer-${id}-link`}
              onClick={() => scrollToId(id)}
              className="text-parchment/70 hover:text-gold-light transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="flex items-center gap-3">
          {activeSocials.length > 0 ? (
            activeSocials.map(([k, Icon]) => (
              <a
                key={k}
                data-testid={`social-${k}`}
                href={social[k]}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={k}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold-light transition-colors"
              >
                <Icon size={18} />
              </a>
            ))
          ) : (
            <span className="text-xs text-parchment/40">Social links coming soon</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-parchment/50 mr-1">Share:</span>
          <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold-light transition-colors"><Facebook size={16} /></a>
          <a data-testid="share-x" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold-light transition-colors"><Twitter size={16} /></a>
          <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold-light transition-colors"><MessageCircle size={16} /></a>
          <button data-testid="share-copy" onClick={copyLink} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold-light transition-colors"><LinkIcon size={16} /></button>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-parchment/50">
        <span>© {new Date().getFullYear()} Charles McKuhn Ministries — a registered 501(c)(3) non-profit (EIN: 43-2446994), under Ascension Apostolic Ministries. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <span data-testid="footer-stats" className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Eye size={13} /> {stats?.views ?? 0} views</span>
            <span className="flex items-center gap-1"><Heart size={13} /> {stats?.follows ?? 0} followers</span>
          </span>
          <a href="mailto:charlesmckuhn99@gmail.com" className="hover:text-gold-light transition-colors">charlesmckuhn99@gmail.com</a>
          <Link to="/login" data-testid="footer-admin-link" className="inline-flex items-center gap-1 text-parchment/40 hover:text-gold-light transition-colors">
            Admin
          </Link>
        </div>
      </div>
    </div>
  </footer>
  );
};
